import { type MouseEvent } from 'react';

import { COLOR_TOKENS, type ColorToken } from '@backrs_ui/common-ui/src/designSystem/colors/colors';

import { Box, type SxProps, type Theme, Typography } from '@mui/material';

export type LegendRowProps = Readonly<{
  color: ColorToken;
  label: string;
  pct?: number;
  /** Underlying count; when given, a non-zero count whose pct rounds to 0 shows as '<1%'. */
  count?: number;
  id?: string;
  onClick?: () => void;
}>;

const pctText = (pct: number, count: number | undefined): string =>
  pct === 0 && count !== undefined && count > 0 ? '<1%' : `${pct}%`;

/** A colour dot and label, with an optional percent chip. */
export const LegendRow = ({ color, label, pct, count, id, onClick }: LegendRowProps) => (
  <Box
    id={id}
    sx={rowSx(pct === undefined, Boolean(onClick))}
    onClick={
      onClick &&
      ((event: MouseEvent) => {
        event.stopPropagation();
        onClick();
      })
    }
  >
    <Box sx={dotSx} style={{ backgroundColor: COLOR_TOKENS[color] }} />
    <Typography variant="text-sm-medium" sx={labelSx}>
      {label}
    </Typography>
    {pct === undefined ? null : (
      <Box sx={pctChipSx}>
        <Typography variant="text-xs-medium" sx={pctChipTextSx}>
          {pctText(pct, count)}
        </Typography>
      </Box>
    )}
  </Box>
);

const dotSx: SxProps<Theme> = ({ backrs }) => ({
  width: 10,
  height: 10,
  borderRadius: backrs.radius['radius-full'],
  flexShrink: 0,
});

/** Without a chip the row is a compact inline item; with one it stretches to fit the chip. */
const rowSx =
  (isItem: boolean, isClickable: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: backrs.spacing[isItem ? 'spacing-sm' : 'spacing-md'],
    ...(isItem && { flexShrink: 0 }),
    ...(isClickable && {
      cursor: 'pointer',
      borderRadius: backrs.radius['radius-sm'],
      '&:hover': {
        backgroundColor: backrs.colors['Colors/Background/bg-secondary-hover'],
      },
    }),
  });

const labelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
  whiteSpace: 'nowrap',
});

const pctChipSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  paddingX: backrs.spacing['spacing-md'],
  paddingY: backrs.spacing['spacing-xxs'],
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const pctChipTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});
